(() => {
  const settings = window.HydroHi || {};
  const strings = settings.strings || {};

  const formatMoney = (cents, currency = 'GBP') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch {
      return `£${(cents / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const drawer = document.querySelector('#CartDrawer');
  const drawerBody = document.querySelector('[data-cart-drawer-body]');
  const toast = document.querySelector('[data-cart-toast]');

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.toggleAttribute('hidden', count === 0);
    });
  };

  const renderCart = (cart) => {
    updateCartCount(cart.item_count);
    if (!drawerBody) return;

    if (!cart.item_count) {
      drawerBody.innerHTML = `
        <div class="cart-drawer__empty">
          <p>${escapeHtml(strings.emptyCart || 'Your cart is empty.')}</p>
          <a class="button button--primary" href="${escapeHtml(settings.allProductsUrl || '/collections/all')}">${escapeHtml(strings.shopHydroHi || 'Shop HydroHi')}</a>
        </div>`;
      return;
    }

    const items = cart.items.map((item) => {
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}&width=160" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy">`
        : '<div class="cart-line__placeholder" aria-hidden="true">H</div>';

      return `
        <article class="cart-line">
          ${image}
          <div class="cart-line__info">
            <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHtml(item.variant_title)}</small>` : ''}
            <span>${escapeHtml(strings.quantityShort || 'Qty')} ${item.quantity}</span>
          </div>
          <strong>${formatMoney(item.final_line_price, cart.currency)}</strong>
        </article>`;
    }).join('');

    const launchOffer = settings.launchCode
      ? `<div class="cart-drawer__offer">
          <span>${escapeHtml(strings.launchOffer || '10% launch offer')}</span>
          <button type="button" class="hh-code" data-copy-code="${escapeHtml(settings.launchCode)}" data-copied-label="${escapeHtml(strings.copied || 'COPIED')}">${escapeHtml(settings.launchCode)}</button>
        </div>`
      : '';

    drawerBody.innerHTML = `
      <div class="cart-lines">${items}</div>
      <div class="cart-drawer__footer">
        ${launchOffer}
        <div class="cart-drawer__subtotal">
          <span>${escapeHtml(strings.subtotal || 'Subtotal')}</span>
          <strong>${formatMoney(cart.total_price, cart.currency)}</strong>
        </div>
        <p>${escapeHtml(strings.shippingAtCheckout || 'Taxes and shipping calculated at checkout.')}</p>
        <a class="button button--primary button--full" href="${escapeHtml(settings.checkoutUrl || '/checkout')}">${escapeHtml(strings.checkout || 'Checkout')}</a>
        <a class="text-link cart-drawer__view" href="${escapeHtml(settings.cartUrl)}">${escapeHtml(strings.viewCart || 'View cart')}</a>
      </div>`;
  };

  const getCart = async ({ fresh = false } = {}) => {
    const cacheBuster = fresh ? `?t=${Date.now()}` : '';
    const response = await fetch(`${settings.cartUrl}.js${cacheBuster}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      cache: fresh ? 'no-store' : 'default'
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  };

  const openCart = async () => {
    if (!drawer) return;
    if (typeof drawer.showModal === 'function') drawer.showModal();
    try {
      renderCart(await getCart());
    } catch {
      window.location.assign(settings.cartUrl);
    }
  };

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2400);
  };

  const copyCode = async (button) => {
    const code = button.dataset.copyCode;
    const original = button.textContent;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const input = document.createElement('textarea');
      input.value = code;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }

    button.textContent = button.dataset.copiedLabel || strings.copied || 'COPIED';
    showToast(strings.codeCopied || `${code} copied`);
    window.setTimeout(() => { button.textContent = original; }, 1600);
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-cart-open]');
    const closeButton = event.target.closest('[data-cart-close]');
    const scrollButton = event.target.closest('[data-scroll-to]');
    const codeButton = event.target.closest('[data-copy-code]');

    if (openButton) {
      event.preventDefault();
      openCart();
    }

    if (closeButton && drawer) drawer.close();
    if (codeButton) copyCode(codeButton);

    if (scrollButton) {
      const target = document.querySelector(scrollButton.dataset.scrollTo);
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });

  document.addEventListener('change', (event) => {
    const choice = event.target.closest('[data-hh-quantity-choice]');
    if (!choice) return;

    const group = choice.closest('fieldset');
    if (group) {
      group.querySelectorAll('label').forEach((label) => label.classList.remove('is-selected'));
      choice.closest('label')?.classList.add('is-selected');
    }

    const form = document.getElementById(choice.dataset.formId);
    const quantity = form?.querySelector('[data-hh-form-quantity]');
    if (quantity) quantity.value = choice.value;
  });

  if (drawer) {
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) drawer.close();
    });
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-quick-add]');
    if (!form || !window.fetch) return;

    event.preventDefault();
    const buttons = Array.from(document.querySelectorAll(`[form="${form.id}"]`)).concat(Array.from(form.querySelectorAll('[type="submit"]')));
    const labels = buttons.map((button) => button.textContent);
    buttons.forEach((button) => {
      button.disabled = true;
      button.textContent = strings.adding || 'Adding…';
    });

    try {
      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));
      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);

      if (!Number.isInteger(variantId) || variantId <= 0) throw new Error('Invalid product variant');

      const response = await fetch(`${settings.cartAddUrl}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({
          items: [{ id: variantId, quantity }]
        })
      });
      if (!response.ok) throw new Error('Add to cart failed');
      const added = await response.json();
      const product = added.items?.[0] || added;
      const cart = await getCart({ fresh: true });
      renderCart(cart);
      showToast(`${product.product_title || 'HydroHi'} ${strings.addedToCart || 'added to cart'}`);
      if (drawer && typeof drawer.showModal === 'function') drawer.showModal();
    } catch {
      HTMLFormElement.prototype.submit.call(form);
    } finally {
      buttons.forEach((button, index) => {
        button.disabled = false;
        button.textContent = labels[index];
      });
    }
  });

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 })
    : null;

  document.querySelectorAll('[data-reveal]').forEach((element) => {
    if (revealObserver) revealObserver.observe(element);
    else element.classList.add('is-visible');
  });
})();
